import { TEST_ID_WATCHLIST } from "e2e/testId";
import React, { memo } from "react";
import BouncyCheckbox from "react-native-bouncy-checkbox";

import { CheckboxGroup } from "@/components/atoms/Checkbox";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";

/**
 * Props for the SearchResultsItemAddRemoveButtonView component
 */
interface Props {
  watchlist: WatchlistWatchlistMetaFragment;
  onChange: () => void;
  isPlayerSelected: boolean;
  loading?: boolean;
  isSmallScreen: boolean;
  isDark: boolean;
}

const BORDER_WIDTH = 0.4;

/**
 * View component that renders a checkbox button for adding or removing players from watchlists
 * @param root0 - Component props
 * @param root0.watchlist - Watchlist data fragment containing watchlist details
 * @param root0.onChange - Callback function invoked when checkbox state changes
 * @param root0.isPlayerSelected - Boolean flag indicating if the player is currently in the watchlist
 * @param root0.loading - Boolean flag indicating if an operation is in progress
 * @param root0.isSmallScreen - Boolean flag indicating if the device has a small screen
 * @param root0.isDark - Boolean flag indicating if dark mode is active
 */
const SearchResultsItemAddRemoveButtonView = ({
  watchlist,
  onChange,
  isPlayerSelected,
  loading,
  isSmallScreen,
  isDark,
}: Props) => (
  <Pressable disabled={loading} onPress={onChange}>
    <Stack
      direction="horizontal"
      className={`items-center gap-0 ${!isSmallScreen ? "px-4" : "px-5"}`}
    >
      <CheckboxGroup
        aria-label="checkbox group"
        onChange={onChange}
        value={isPlayerSelected ? [watchlist.id] : []}
      >
        <BouncyCheckbox
          testID={
            isPlayerSelected
              ? TEST_ID_WATCHLIST.CHECKBOX_CHECKED
              : TEST_ID_WATCHLIST.CHECKBOX_UNCHECKED
          }
          isChecked={isPlayerSelected}
          size={isSmallScreen ? 20 : 16}
          fillColor="#32b2f1"
          /*NOTE: Using info-500 color (#0ea5e9)*/
          innerIconStyle={{
            borderWidth: BORDER_WIDTH,
            borderColor: isDark ? "white" : "#0DA6F2",
          }}
          disabled={loading}
          onPress={onChange}
        />
      </CheckboxGroup>
      <Text
        variant="unstyled"
        className={
          isPlayerSelected && watchlist.name === "Default"
            ? `mr-6 font-body ${!isSmallScreen ? "text-title-sm" : "text-title"} text-status-info`
            : `mr-6 font-body ${!isSmallScreen ? "text-title-sm" : "text-title"}`
        }
      >
        {watchlist.name}
      </Text>
    </Stack>
  </Pressable>
);
SearchResultsItemAddRemoveButtonView.displayName =
  "SearchResultsItemAddRemoveButtonView";
export default memo(SearchResultsItemAddRemoveButtonView);
